import { ApolloError, useApolloClient, useMutation } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import React, { useState } from 'react';
import { useHistory } from 'react-router';

import { Form } from 'src/components/common/form/Form';
import { CloseModalFn } from 'src/components/common/URLModal';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { deleteGraphMutation } from './DeleteGraphHelpers';
import { DeleteGraphModalContent } from './DeleteGraphModalContent';

export const DeleteGraphConfirmationModal = ({
  service,
  onClose,
  isOpen,
  setGeneralError,
}: {
  service: GraphQLTypes.DeleteGraphCardServiceFragment;
  isOpen: boolean;
  onClose: CloseModalFn;
  setGeneralError: (e: ApolloError) => void;
}) => {
  if (!service.account)
    throw new Error('Current Graph has no owner Organization.');

  const history = useHistory();
  const client = useApolloClient();

  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const canDelete = service.title && inputValue === service.title;

  const [deleteGraph, { loading }] = useMutation<
    GraphQLTypes.Graph__DeleteGraphMutation,
    GraphQLTypes.Graph__DeleteGraphMutationVariables
  >(deleteGraphMutation, {
    variables: { serviceId: service.id },
    onCompleted: (mutationResponseData) => {
      onClose();

      client.cache.evict({
        id: client.cache.identify({
          __typename: mutationResponseData.service?.service.__typename,
          id: mutationResponseData.service?.service.id,
        }),
      });
      client.cache.gc();

      history.push(
        `/org/${
          mutationResponseData?.service?.service.account?.id ??
          service?.account?.id
        }`,
      );
    },
    onError: (e) => setGeneralError(e),
  });

  return (
    <Modal size="lg" isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <Form
        className="bg-stripes-red items-center"
        onSubmit={async (event) => {
          event.preventDefault();
          try {
            if (canDelete) {
              await deleteGraph();
            } else {
              setErrorMessage(`Your input does not match ${service.title} `);
            }
          } catch (exception) {
            setErrorMessage((exception as Error).message);
          }
        }}
        noValidate
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              Are you sure you want to delete the {service.title} graph?
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <DeleteGraphModalContent
              service={service}
              errorMessage={errorMessage}
            />
            <Input
              className="w-full border-error bg-input focus:border-error"
              autoFocus={true}
              isDisabled={loading}
              onChange={(event) => {
                setInputValue(event.target.value);
              }}
            />
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button
                variant="secondary"
                isDisabled={loading}
                onClick={() => onClose()}
                type="button"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                isDisabled={!canDelete}
                isLoading={loading}
                loadingText="Yes, delete my graph"
                type="submit"
              >
                Yes, delete my graph
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
};
